/* verqo — Seller Pricing page (preview build) */
import { useState } from "react";
import { ArrowRight, CheckCircle2, Star } from "lucide-react";
import { SubPageLayout, Reveal, Eyebrow, CTABanner, FAQ } from "../../../components/marketing/SubPageBlocks";
import PageSEO from "../../../components/marketing/PageSEO";

const PLANS = [
  {
    id: "starter", name: "Starter", sub: "Solo founders & first wholesale accounts",
    monthly: 0, annual: 0,
    heading: "What's included",
    bullets: [
      "Storefront + catalog (100 SKUs)",
      "10 active buyers",
      "Standard payments (ACH, card)",
      "Community support",
    ],
    cta: "Start free",
  },
  {
    id: "growth", name: "Growth", sub: "Brands doing $1-10M in wholesale",
    monthly: 249, annual: 224,
    eyebrow: "Most popular", featured: true,
    heading: "Everything in Starter, plus",
    bullets: [
      "500 SKUs · 100 active buyers",
      "Net-30/60/90 credit terms",
      "Auto-invoicing + reconciliation",
      "Verqo Advance (Same-day cash)",
      "Priority support",
    ],
    cta: "Start Growth trial",
  },
  {
    id: "scale", name: "Scale", sub: "$10-100M distributors & multi-brand",
    monthly: 999, annual: 899,
    heading: "Everything in Growth, plus",
    bullets: [
      "Unlimited SKUs · 500 active buyers",
      "Multi-brand / multi-warehouse",
      "EDI 850/810/856/855",
      "Buyer scores + auto-limit rules",
      "Freight & LTL desk",
      "Dedicated CSM",
    ],
    cta: "Talk to sales",
  },
  {
    id: "enterprise", name: "Enterprise", sub: "$100M+ distributors, marketplaces, 3PLs",
    monthly: null, annual: null,
    heading: "Everything in Scale, plus",
    bullets: [
      "Custom SLAs",
      "White-label API access",
      "SOC 2 controls & SSO",
      "Custom sponsor bank arrangements",
      "24/7 phone support",
    ],
    cta: "Contact us",
  },
];

const FAQ_ITEMS = [
  { q: "Do buyers pay to use Verqo?",  a: "No. Buyers use Verqo for free — always. You pay to run the wholesale-OS." },
  { q: "How does Verqo Advance work?", a: "We fund your invoice within 24 hours at 1.5-3.5% of face value. Buyer pays us on their terms. No recourse to you." },
  { q: "Can I switch plans anytime?",  a: "Yes. Upgrade or downgrade any month. Annual plans convert on renewal." },
  { q: "Is there an onboarding fee?",  a: "No. Growth and Scale plans include free onboarding with a dedicated CSM." },
];

export default function SellerPricingPreview() {
  const [billing, setBilling] = useState("monthly");

  return (
    <SubPageLayout modeOverride="seller">
      <PageSEO
        title="Pricing · verqo"
        description="One platform, four simple plans. Starter is free, Growth is $249/mo, Scale is $999/mo, Enterprise is custom. Buyers use Verqo free, forever."
        path="/pricing"
      />
      <section className="relative pt-20 md:pt-28 pb-8 px-6 md:px-10 max-w-[1280px] mx-auto text-center" data-testid="seller-pricing-hero">
        <Reveal><Eyebrow>Pricing · Sellers</Eyebrow></Reveal>
        <Reveal delay={0.08}>
          <h1 className="mt-6 text-[36px] md:text-[64px] leading-[1.02] tracking-tight font-semibold text-slate-900">
            One platform. <span className="text-slate-400 italic">Four simple plans.</span>
          </h1>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-6 max-w-2xl mx-auto text-[15px] md:text-[16px] leading-relaxed text-slate-600">
            Easy entrance. Pay only for the apps and services you use. <strong className="text-slate-900">Buyers are free, forever.</strong>
          </p>
        </Reveal>

        <Reveal delay={0.22}>
          <div className="mt-10 flex items-center justify-center gap-3" data-testid="seller-pricing-toggle">
            <button
              type="button"
              onClick={() => setBilling("monthly")}
              data-testid="seller-billing-monthly"
              className={`px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all ${billing === "monthly" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setBilling("annual")}
              data-testid="seller-billing-annual"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-semibold transition-all ${billing === "annual" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              Annual
              <span className={`text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full ${billing === "annual" ? "bg-lime-300 text-slate-900" : "bg-lime-100 text-lime-800"}`}>Save 10%</span>
            </button>
          </div>
        </Reveal>
      </section>

      <section className="relative py-10 px-6 md:px-10 max-w-[1300px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLANS.map((t, i) => {
            const shownPrice = billing === "annual" ? t.annual : t.monthly;
            return (
              <Reveal key={t.id} delay={i * 0.06}>
                <div
                  data-testid={`seller-pricing-card-${t.id}`}
                  className={`h-full rounded-2xl p-7 md:p-8 flex flex-col ${
                    t.featured ? "border-2 border-[#0c1612] bg-white shadow-[0_24px_64px_-24px_rgba(132,204,22,0.35)] -translate-y-2"
                                : "border border-slate-200 bg-white"
                  }`}
                >
                  {t.eyebrow && (
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.18em] font-semibold w-fit mb-4 bg-lime-300 text-slate-900">
                      <Star className="w-3 h-3" /> {t.eyebrow}
                    </div>
                  )}
                  <div className="text-[11px] uppercase tracking-[0.22em] text-slate-500">{t.name}</div>
                  <div className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">{t.sub}</div>
                  <div className="mt-6 flex items-baseline gap-1.5">
                    {shownPrice === null ? (
                      <span className="text-[32px] font-semibold tracking-tight text-slate-900 leading-none">Custom</span>
                    ) : (
                      <>
                        <span className="text-[44px] font-semibold tracking-tight text-slate-900 leading-none">${shownPrice}</span>
                        <span className="text-[13px] text-slate-500">/ month</span>
                      </>
                    )}
                  </div>
                  <a
                    href="/waitlist?as=seller"
                    data-testid={`seller-pricing-cta-${t.id}`}
                    className={`mt-6 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg text-sm font-semibold transition-colors ${
                      t.featured ? "bg-[#0c1612] text-white hover:opacity-90"
                                 : "bg-white border border-slate-300 text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    {t.cta} <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                  <div className="mt-7 pt-6 border-t border-slate-200">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">{t.heading}</div>
                    <ul className="space-y-2.5 flex-1">
                      {t.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-2 text-[13px] text-slate-700 leading-relaxed">
                          <CheckCircle2 className="w-3.5 h-3.5 text-lime-600 mt-0.5 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <FAQ items={FAQ_ITEMS} />

      <CTABanner
        title="Ready to see it running on your catalog?"
        subtitle="Book a 20-minute demo. We'll import your buyer list and show you your live DSO impact."
        primary={{ label: "Get started", href: "/waitlist?as=seller" }}
        secondary={{ label: "See how it works", href: "/company/how-it-works" }}
      />
    </SubPageLayout>
  );
}
